import { useEffect, useState } from 'react';
import { Box, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api.js';
import { showAlert, showConfirm } from '../lib/tg.js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemGroupContent,
  ItemGroupEmpty,
  ItemGroupHeader,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item';
import { ErrorBanner } from '@/components/feedback';

interface MdEntity {
  id: string;
  name: string;
  description: string;
  frontmatter: Record<string, unknown>;
  body: string;
  updatedAt?: string | null;
}

interface MarkdownEntityViewProps {
  /** Path prefix without leading slash, e.g. 'subagents'. */
  prefix: 'subagents';
  /** Heading shown in the view. */
  title: string;
}

export function MarkdownEntityView({ prefix, title }: MarkdownEntityViewProps) {
  const [items, setItems] = useState<MdEntity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<MdEntity | 'new' | null>(null);

  const reload = async () => {
    try {
      setItems(await api.get<MdEntity[]>(`/api/${prefix}`));
    } catch (e) {
      setError((e as { message: string }).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [prefix]);

  return (
    <ItemGroup>
      {error && <ErrorBanner>{error}</ErrorBanner>}
      <ItemGroupHeader
        title={title}
        action={
          <Button size="sm" onClick={() => setEditing('new')}>
            <Plus />
            New
          </Button>
        }
      />

      <ItemGroupContent>
        {loading && items.length === 0 && <ItemGroupEmpty>loading…</ItemGroupEmpty>}
        {!loading && items.length === 0 && <ItemGroupEmpty>none yet</ItemGroupEmpty>}

        {items.map((s) => (
          <Item key={s.id} interactive onClick={() => setEditing(s)}>
            <ItemMedia>
              <Box />
            </ItemMedia>
            <ItemContent>
              <ItemTitle>{s.name}</ItemTitle>
              {s.description && <ItemDescription>{s.description}</ItemDescription>}
              {s.body && (
                <code className="mt-1 block rounded bg-muted px-2 py-1 text-[11px]">
                  {s.body.slice(0, 140)}
                  {s.body.length > 140 ? '…' : ''}
                </code>
              )}
            </ItemContent>
            <ItemActions>
              <Button
                variant="ghost"
                size="icon"
                onClick={async () => {
                  if (!(await showConfirm(`Delete "${s.name}"?`))) return;
                  try {
                    await api.del(`/api/${prefix}/${s.id}`);
                    void reload();
                  } catch (e) {
                    void showAlert((e as { message: string }).message);
                  }
                }}
              >
                <Trash2 />
              </Button>
            </ItemActions>
          </Item>
        ))}
      </ItemGroupContent>

      <MdEditorDialog
        open={editing !== null}
        prefix={prefix}
        initial={editing === 'new' ? null : (editing as MdEntity | null)}
        onOpenChange={(open) => !open && setEditing(null)}
        onSaved={() => {
          setEditing(null);
          void reload();
        }}
      />
    </ItemGroup>
  );
}

function MdEditorDialog({
  open,
  prefix,
  initial,
  onOpenChange,
  onSaved,
}: {
  open: boolean;
  prefix: 'subagents';
  initial: MdEntity | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [frontmatterText, setFrontmatterText] = useState('{}');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? '');
    setDescription(initial?.description ?? '');
    setFrontmatterText(initial ? JSON.stringify(initial.frontmatter, null, 2) : '{}');
    setBody(initial?.body ?? '');
    setErr(null);
  }, [open, initial]);

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const frontmatter = JSON.parse(frontmatterText);
      const payload = { name, description, frontmatter, body };
      if (initial) await api.patch(`/api/${prefix}/${initial.id}`, payload);
      else await api.post(`/api/${prefix}`, payload);
      onSaved();
    } catch (e) {
      setErr((e as { message: string }).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{initial ? `Edit "${initial.name}"` : 'New'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="md-name">Name</Label>
            <Input id="md-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="md-desc">Description</Label>
            <Input id="md-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="md-fm">Frontmatter (JSON)</Label>
            <Textarea
              id="md-fm"
              value={frontmatterText}
              onChange={(e) => setFrontmatterText(e.target.value)}
              className="min-h-[80px] font-mono text-xs"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="md-body">Body (markdown)</Label>
            <Textarea
              id="md-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="min-h-[240px] font-mono text-xs"
            />
          </div>
          {err && <ErrorBanner>{err}</ErrorBanner>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={busy || !name} onClick={save}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
